// DXF export orchestrator. Derives every piece of geometry from the same code
// paths the canvas renders with: symbol nodes resolve their SVG exactly like
// CustomNode (selectedVariantSrc → resolveSymbolSrc) and become BLOCK+INSERT
// pairs; edges anchor at true handle positions and route like OrthogonalEdge;
// buses, text boxes, and nombre labels mirror their node components. Screen
// coordinates are flow px, Y-flipped once via the page height.
//
// Asset access is injected (browser: fetch; tests: fs) so the builder runs
// headless. Nothing silently disappears: any node whose SVG cannot be loaded
// or parsed exports a placeholder box + label and a warning for the caller.

import { screenToCad, symbolInsertPlacement } from './transform2d';
import { svgToSymbolGeometry } from './svgSymbol';
import { DxfDocument, LAYER_CONDUCTORS, LAYER_FRAME, LAYER_LABELS, LAYER_SYMBOLS } from './writer';
import { edgeAnchor, edgeRoutePoints, nodeBox } from './anchors';
import { getUsedSymbolDrawerItems } from '../../symbolDrawerItems';

const DEFAULT_LABEL_FONT_PX = 38;
const PX_PER_CM = 37.7952755906;
const BASE_PX = 100;
const TEXTBOX_BASE_PADDING_X = 18;
const TEXTBOX_BASE_PADDING_Y = 16;
const TEXTBOX_PLACEHOLDER = 'Agrega texto en el inspector';

const UNIT_SUFFIX_BY_KEY = {
  ratedVoltage: 'V',
  voltage: 'V',
  primaryVoltage: 'V',
  secondaryVoltage: 'V',
  tertiaryVoltage: 'V',
  voltageCurrent: 'V',
  ratedPower: 'kVA',
  power: 'kVA',
  ratedCurrent: 'A',
  shortCircuitCurrent: 'A',
  length: 'm',
  breakingCapacity: 'kA',
  protectionBreakingCapacity: 'kA',
  efficiency: '%',
  threePhaseLines: 'A',
  lineasTrifasicas: 'A',
  threePhaseIcc: 'A',
  lineToGroundIcc: 'A',
  threePhaseICC: 'A',
  lineToGroundICC: 'A',
  ampacity: 'A',
  ampacidad: 'A',
  impedance: '%',
  neutralImpedance: 'Ohm',
};

const CURRENT_UNIT_KEYS = new Set([
  'ratedCurrent',
  'shortCircuitCurrent',
  'threePhaseLines',
  'lineasTrifasicas',
  'threePhaseIcc',
  'lineToGroundIcc',
  'threePhaseICC',
  'lineToGroundICC',
  'ampacity',
  'ampacidad',
]);

const VOLTAGE_UNIT_KEYS = new Set([
  'ratedVoltage',
  'voltage',
  'primaryVoltage',
  'secondaryVoltage',
  'tertiaryVoltage',
  'voltageCurrent',
]);

const POWER_UNIT_KEYS = new Set([
  'ratedPower',
  'power',
]);

const HIDDEN_CANVAS_ATTRIBUTES_BY_SYMBOL = {
  DistributionBoard: new Set([
    'headerData',
    'rowData',
    'columnWidths',
    'showDistributionBoardPreview',
    'distributionBoardPreviewScale',
    'distributionBoardPreviewPosition',
  ]),
};

const stripDiacritics = (value = '') =>
  value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const normalizeText = (value) =>
  typeof value === 'string' ? stripDiacritics(value).trim().toLowerCase() : '';

const resolveSymbolSrc = (svgSrc) => {
  if (!svgSrc || typeof svgSrc !== 'string') return null;
  const trimmed = svgSrc.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('data:')) return trimmed;
  return `/${trimmed.replace(/^\.?\//, '')}`;
};

const selectedVariantSrc = (data) => {
  const variants = Array.isArray(data?.variants) ? data.variants : [];
  if (variants.length > 0) {
    const selected = variants.find((variant) => variant.selected) || variants[0];
    if (selected?.diagramSvgSrc) return selected.diagramSvgSrc;
  }
  return data?.svgSrc;
};

const scaleFactor = (scaleCm) => {
  const cm = typeof scaleCm === 'number' && scaleCm > 0 ? scaleCm : BASE_PX / PX_PER_CM;
  const factor = (cm * PX_PER_CM) / BASE_PX;
  return Math.max(0.4, Math.min(6, factor));
};

const computeTextBoxStyle = (data = {}) => {
  const attrs = data?.attributes || {};
  const align = ['left', 'right'].includes(String(attrs.textAlign?.value || '').trim().toLowerCase())
    ? String(attrs.textAlign.value).trim().toLowerCase()
    : 'center';
  const factor = scaleFactor(data?.scaleCm);
  return {
    padding: `${TEXTBOX_BASE_PADDING_Y * factor}px ${TEXTBOX_BASE_PADDING_X * factor}px`,
    fontSize: typeof data?.textSizePx === 'number' && data.textSizePx > 0 ? data.textSizePx : DEFAULT_LABEL_FONT_PX,
    textAlign: align,
  };
};

const textBoxDisplay = (data = {}) => {
  const value = typeof data?.attributes?.textContent?.value === 'string' ? data.attributes.textContent.value : '';
  const isPlaceholder = value.trim().length === 0;
  return { text: isPlaceholder ? TEXTBOX_PLACEHOLDER : value, isPlaceholder };
};

/** CSS font-size is the em box; capital glyphs are ~72% of it. DXF TEXT height
 * is the cap height, so this ratio keeps exported text visually matched. */
export const TEXT_CAP_RATIO = 0.72;

/** CSS line-height "normal" for the textbox font stack. */
const TEXTBOX_LINE_HEIGHT = 1.2;

/** lineHeight of nombre labels (CustomNode/BusNode set lineHeight: 1.1). */
const LABEL_LINE_HEIGHT = 1.1;

/** Rough average glyph advance for system-ui, used only to detect overflow. */
const AVG_CHAR_WIDTH_EM = 0.5;

/** Gap between node box and its nombre label (CustomNode marginLeft). */
const LABEL_GAP_PX = 8;

/** Gap between bus bar and its label (BusNode marginBottom). */
const BUS_LABEL_GAP_PX = 6;

/** Canvas font size of the failed-SVG fallback tile (CustomNode). */
const PLACEHOLDER_FONT_PX = 10;
const BOX_SYMBOL_LABEL = 'Box';
const DESIGN_NODE_TYPES = new Set(['boxNode', 'straightLineNode', 'textBoxNode', 'symbolDrawerNode']);
const DESIGN_SYMBOL_LABELS = new Set([BOX_SYMBOL_LABEL, 'StraightLine', 'SymbolDrawer']);
const FRAME_COLUMNS = 15;
const FRAME_ROWS = 11;
const FRAME_ROW_LABELS = 'ABCDEFGHIJK'.split('');
const TEMPLATE_IMAGE_SCALE_MIN = 120;
const TEMPLATE_IMAGE_SCALE_MAX = 2000;
const TEMPLATE_IMAGE_SCALE_DEFAULT = 320;
const TEMPLATE_IMAGE_RATIO_DEFAULT = 'square';
const TEMPLATE_TEXT_WIDTH_DEFAULT = 520;
const TEMPLATE_ANCHOR_HORIZONTAL_PADDING = 32;
const TEMPLATE_ANCHOR_VERTICAL_PADDING = 24;
const TEMPLATE_ANCHOR_ROW_GAP = 12;
const TEMPLATE_ANCHOR_COLUMN_GAP = 12;
const TEMPLATE_ANCHOR_MIN_WIDTH = 260;
const TEMPLATE_ANCHOR_MIN_HEIGHT = 160;
const TEMPLATE_ANCHOR_MIN_COLUMN_WIDTH = 120;
const TEMPLATE_TEXT_BLOCK_BASE_WIDTH = 360;
const TEMPLATE_TEXT_BLOCK_BASE_HEIGHT = 140;
const TEMPLATE_FLOATING_TEXT_FONT_PX = 40;

const resolveUnitSuffix = (fieldKey, fieldLabel) => {
  if (fieldKey && UNIT_SUFFIX_BY_KEY[fieldKey]) {
    return UNIT_SUFFIX_BY_KEY[fieldKey];
  }
  const normalized = normalizeText(fieldLabel);
  if (!normalized) return null;
  if (normalized.includes('voltaje nominal') || normalized === 'voltaje' || normalized.startsWith('voltaje ')) return 'V';
  if (normalized.includes('potencia nominal')) return 'kVA';
  if (normalized.includes('eficiencia')) return '%';
  if (normalized.includes('corriente nominal')) return 'A';
  if (normalized.includes('corriente de corto circuito') || normalized.includes('corto circuito')) return 'A';
  if (normalized.includes('icc trifasica') || normalized.includes('icc de linea a tierra')) return 'A';
  if (normalized === 'longitud') return 'm';
  if (normalized.includes('capacidad interruptiva')) return 'kA';
  if (normalized.includes('lineas trifasicas')) return 'A';
  if (normalized.includes('impedancia de neutro')) return 'Ohm';
  if (normalized.startsWith('impedancia') || normalized.includes('impedancia')) return '%';
  return null;
};

const formatAttributeValue = (key, label, attribute, symbolLabel) => {
  if (!attribute) return '';
  const rawValue = attribute.value;
  if (rawValue === undefined || rawValue === null) return '';
  const rawText = typeof rawValue === 'string' ? rawValue : String(rawValue);
  const hasTrailingSpace = /\s$/.test(rawText);
  const text = rawText.trim();
  if (!text) return '';
  const disableCurrentUnitToggle = symbolLabel === 'Fuse';
  const explicitUnit = (() => {
    if (key === 'phaseGauge' || key === 'neutralGauge' || key === 'groundGauge') {
      const allowed = ['AWG', 'KCMIL', 'mm2'];
      return allowed.includes(attribute.unitSuffix) ? attribute.unitSuffix : 'KCMIL';
    }
    if (POWER_UNIT_KEYS.has(key)) {
      const allowed = ['HP', 'kW', 'kVA'];
      return allowed.includes(attribute.unitSuffix) ? attribute.unitSuffix : 'kVA';
    }
    if (VOLTAGE_UNIT_KEYS.has(key)) {
      const allowed = ['V', 'kV'];
      return allowed.includes(attribute.unitSuffix) ? attribute.unitSuffix : 'V';
    }
    if (CURRENT_UNIT_KEYS.has(key)) {
      if (disableCurrentUnitToggle) return 'A';
      const allowed = ['A', 'kA'];
      return allowed.includes(attribute.unitSuffix) ? attribute.unitSuffix : 'A';
    }
    if (key === 'std' || key === 'gfd') {
      return attribute.unitSuffix === 'I2t OFF' ? 'I2t OFF' : 'I2t ON';
    }
    if (key === 'length') {
      const allowed = ['m', 'km', 'pies'];
      return allowed.includes(attribute.unitSuffix) ? attribute.unitSuffix : 'm';
    }
    return attribute.unitSuffix;
  })();
  if (explicitUnit) return `${text} ${explicitUnit}`.trim();
  const suffix = resolveUnitSuffix(key, label);
  if (!suffix) return text;
  const joiner = hasTrailingSpace ? ' ' : '';
  return `${text}${joiner}${suffix}`;
};

const labelOf = (node) => {
  const attrs = node.data?.attributes || {};
  const hiddenKeys = HIDDEN_CANVAS_ATTRIBUTES_BY_SYMBOL[node.data?.symbolLabel];
  const rows = Object.entries(attrs)
    .filter(([key, attr]) => {
      if (!attr || !attr.isVisible) return false;
      if (hiddenKeys && hiddenKeys.has(key)) return false;
      const raw = attr.value;
      if (raw === undefined || raw === null) return false;
      const text = typeof raw === 'string' ? raw.trim() : String(raw).trim();
      return text.length > 0;
    })
    .sort(([keyA], [keyB]) => {
      if (keyA === 'nombre' && keyB !== 'nombre') return -1;
      if (keyB === 'nombre' && keyA !== 'nombre') return 1;
      return 0;
    })
    .map(([key, attr]) => {
      const rawLabel = attr.label;
      const label = rawLabel ?? key;
      const labelText = typeof rawLabel === 'string' && rawLabel.trim() === '' ? '' : label;
      const formatLabel = labelText || key;
      const value = formatAttributeValue(key, formatLabel, attr, node.data?.symbolLabel);
      if (!value) return null;
      if (node.data?.hideAttributeLabels || !labelText) return value;
      return `${labelText}: ${value}`;
    })
    .filter(Boolean);
  if (rows.length === 0) return null;
  const fontPx = typeof node.data?.textSizePx === 'number' && node.data.textSizePx > 0 ? node.data.textSizePx : DEFAULT_LABEL_FONT_PX;
  return { lines: rows, fontPx };
};

const angleOf = (node) => {
  const raw = node.data?.angle;
  return typeof raw === 'number' ? raw : Number.parseFloat(String(raw ?? '')) || 0;
};

function writeSymbolGeometry(w, geometry, layer) {
  for (const fill of geometry.fills || []) {
    const color = Number.isInteger(fill.aci) ? { aci: fill.aci } : null;
    w.solid(fill.corners, layer, color);
  }
  for (const poly of geometry.polylines) {
    const color = Number.isInteger(poly.aci) ? { aci: poly.aci } : null;
    if (poly.points.length === 2 && !poly.closed) w.line(poly.points[0], poly.points[1], layer, color);
    else w.polyline(poly.points, poly.closed, layer, color);
  }
}

const numericAttr = (attrs, key, fallback) => {
  const numeric = Number(attrs?.[key]?.value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const normalizeTableRows = (table) => {
  const headers = Array.isArray(table?.headers) ? table.headers.map((header) => String(header ?? '')) : [];
  const rows = Array.isArray(table?.rows)
    ? table.rows.map((row) => (
        Array.isArray(row)
          ? headers.map((_, index) => String(row[index] ?? ''))
          : headers.map(() => '')
      ))
    : [];
  return headers.length ? { headers, rows } : null;
};

const stripHtml = (value = '') => String(value).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const wrapPlainText = (text, maxChars) => {
  const normalized = stripHtml(text);
  if (!normalized) return [];
  const limit = Math.max(1, Math.floor(maxChars));
  return normalized.split(/\r?\n/).flatMap((line) => {
    if (line.length <= limit) return [line];
    const chunks = [];
    let remaining = line;
    while (remaining.length > limit) {
      const breakIndex = remaining.lastIndexOf(' ', limit);
      const nextIndex = breakIndex > 0 ? breakIndex : limit;
      chunks.push(remaining.slice(0, nextIndex).trimEnd());
      remaining = remaining.slice(nextIndex).trimStart();
    }
    if (remaining) chunks.push(remaining);
    return chunks;
  });
};

const writeTextInRect = (
  doc,
  text,
  x,
  y,
  width,
  height,
  toCad,
  {
    fontPx = 10,
    paddingX = 4,
    paddingY = 4,
    hAlign = 0,
    vAlign = 1,
    layer = LAYER_LABELS,
    charWidth = AVG_CHAR_WIDTH_EM * 1.25,
  } = {},
) => {
  const contentWidth = Math.max(1, width - paddingX * 2);
  const contentHeight = Math.max(1, height - paddingY * 2);
  const cap = fontPx * TEXT_CAP_RATIO;
  const lineHeight = fontPx * 1.2;
  const maxChars = Math.max(1, Math.floor(contentWidth / (fontPx * charWidth)));
  const lines = wrapPlainText(text, maxChars).slice(0, Math.max(1, Math.floor(contentHeight / lineHeight)));
  const totalTextHeight = lines.length * lineHeight;
  const startY = vAlign === 2
    ? y + paddingY
    : vAlign === 0
      ? y + Math.max(paddingY, contentHeight - totalTextHeight + paddingY)
      : y + paddingY + Math.max(0, (contentHeight - totalTextHeight) / 2);
  lines.forEach((line, index) => {
    if (!line) return;
    const textX = hAlign === 1 ? x + width / 2 : hAlign === 2 ? x + width - paddingX : x + paddingX;
    const point = toCad({ x: textX, y: startY + index * lineHeight + cap });
    doc.entities.text({
      x: point.x,
      y: point.y,
      height: cap,
      content: line,
      layer,
      hAlign,
    });
  });
};

const clampTemplateImageScaleValue = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return TEMPLATE_IMAGE_SCALE_DEFAULT;
  return Math.min(TEMPLATE_IMAGE_SCALE_MAX, Math.max(TEMPLATE_IMAGE_SCALE_MIN, numeric));
};

const normalizeTemplateImageRatioValue = (value) => (
  value === 'rectangle' ? 'rectangle' : TEMPLATE_IMAGE_RATIO_DEFAULT
);

export const isDxfDesignNode = (node) => {
  const type = node?.type || 'customNode';
  return DESIGN_NODE_TYPES.has(type) || DESIGN_SYMBOL_LABELS.has(node?.data?.symbolLabel);
};

const templateImageLabel = (block) => (
  block?.image?.name
  || (typeof block?.image?.src === 'string' ? block.image.src.split('/').pop()?.slice(0, 48) : '')
  || 'Imagen'
);

const getTemplateImageSrc = (block) => (
  typeof block?.image?.src === 'string' && block.image.src ? block.image.src : null
);

const isTemplateRasterImageSrc = (src) => (
  /\.(?:png|jpe?g|webp)(?:[?#].*)?$/i.test(String(src || ''))
);

const decodeSvgDataUrl = (src) => {
  const match = /^data:image\/svg\+xml(?:;charset=[^;,]+)?(;base64)?,(.*)$/i.exec(src || '');
  if (!match) return null;
  try {
    return match[1]
      ? atob(match[2])
      : decodeURIComponent(match[2]);
  } catch {
    return null;
  }
};

const loadTemplateSvgText = async (block, loader) => {
  const src = getTemplateImageSrc(block);
  if (!src) return null;
  const trimmed = src.trim();
  if (trimmed.startsWith('<svg')) return trimmed;
  const dataSvg = decodeSvgDataUrl(trimmed);
  if (dataSvg) return dataSvg;
  if (!/\.svg(?:[?#].*)?$/i.test(trimmed)) return null;
  try {
    return await loader.loadText(resolveSymbolSrc(trimmed));
  } catch {
    return null;
  }
};

const templateRowsFromBlocks = (blocks) => {
  const layoutBlocks = Array.isArray(blocks) ? blocks.filter((block) => block?.type !== 'image' && block?.type !== 'text') : [];
  if (layoutBlocks.length === 0) return [];
  const rows = [];
  const rowMap = new Map();
  layoutBlocks.forEach((block, index) => {
    const rowId = typeof block.rowId === 'string' ? block.rowId : block.id;
    let row = rowMap.get(rowId);
    if (!row) {
      row = {
        id: rowId,
        columns: block.rowColumns === 2 ? 2 : 1,
        blocks: [],
        firstIndex: index,
      };
      rowMap.set(rowId, row);
      rows.push(row);
    }
    row.blocks.push(block);
    if (block.rowColumns === 2) row.columns = 2;
  });
  return rows
    .sort((a, b) => a.firstIndex - b.firstIndex)
    .map((row) => ({
      ...row,
      blocks: row.blocks.slice().sort((a, b) => (a?.rowIndex ?? 0) - (b?.rowIndex ?? 0)),
    }));
};

const computeTemplateContentBounds = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return {
      width: TEMPLATE_ANCHOR_MIN_WIDTH,
      height: TEMPLATE_ANCHOR_MIN_HEIGHT,
      innerWidth: Math.max(TEMPLATE_ANCHOR_MIN_WIDTH - TEMPLATE_ANCHOR_HORIZONTAL_PADDING * 2, 0),
    };
  }
  let largestImageWidth = 0;
  rows.forEach((row) => {
    row.blocks.forEach((block) => {
      if (block.type === 'image') {
        largestImageWidth = Math.max(largestImageWidth, clampTemplateImageScaleValue(block.imageScale));
      }
    });
  });
  const textBlockWidth = Math.max(
    largestImageWidth || TEMPLATE_TEXT_BLOCK_BASE_WIDTH,
    TEMPLATE_ANCHOR_MIN_WIDTH - TEMPLATE_ANCHOR_HORIZONTAL_PADDING * 2,
  );
  let maxRowWidth = 0;
  let totalHeight = 0;
  rows.forEach((row) => {
    let rowWidth = 0;
    let rowHeight = 0;
    row.blocks.forEach((block) => {
      if (block.type === 'image') {
        const imageWidth = clampTemplateImageScaleValue(block.imageScale);
        const ratio = normalizeTemplateImageRatioValue(block.imageRatio);
        rowWidth += imageWidth;
        rowHeight = Math.max(rowHeight, ratio === 'rectangle' ? imageWidth * (3 / 4) : imageWidth);
      } else {
        rowWidth += textBlockWidth;
        rowHeight = Math.max(rowHeight, TEMPLATE_TEXT_BLOCK_BASE_HEIGHT);
      }
    });
    if (row.blocks.length > 1) rowWidth += (row.blocks.length - 1) * TEMPLATE_ANCHOR_COLUMN_GAP;
    maxRowWidth = Math.max(maxRowWidth, rowWidth);
    totalHeight += rowHeight;
  });
  if (rows.length > 1) totalHeight += (rows.length - 1) * TEMPLATE_ANCHOR_ROW_GAP;
  const width = Math.max(maxRowWidth + TEMPLATE_ANCHOR_HORIZONTAL_PADDING * 2, TEMPLATE_ANCHOR_MIN_WIDTH);
  const height = Math.max(totalHeight + TEMPLATE_ANCHOR_VERTICAL_PADDING * 2, TEMPLATE_ANCHOR_MIN_HEIGHT);
  return {
    width,
    height,
    innerWidth: Math.max(width - TEMPLATE_ANCHOR_HORIZONTAL_PADDING * 2, 0),
    textWidth: textBlockWidth,
  };
};

function writeBoxNode(doc, node, box, toCad) {
  const attrs = node.data?.attributes || {};
  const width = Math.max(1, numericAttr(attrs, 'width', box.width || 240));
  const height = Math.max(1, numericAttr(attrs, 'height', box.height || 160));
  const inset = Math.max(0.5, numericAttr(attrs, 'strokeWidth', 3) / 2);
  const inner = {
    x: box.x + inset,
    y: box.y + inset,
    width: Math.max(1, width - inset * 2),
    height: Math.max(1, height - inset * 2),
  };
  const table = normalizeTableRows(node.data?.table);
  const columns = table ? table.headers.length : Math.max(1, Math.round(numericAttr(attrs, 'columns', 1)));
  const rows = table ? Math.max(1, table.rows.length + 1) : Math.max(1, Math.round(numericAttr(attrs, 'rows', 1)));
  const corners = [
    { x: inner.x, y: inner.y },
    { x: inner.x + inner.width, y: inner.y },
    { x: inner.x + inner.width, y: inner.y + inner.height },
    { x: inner.x, y: inner.y + inner.height },
  ].map(toCad);
  doc.entities.polyline(corners, true, LAYER_SYMBOLS);

  for (let index = 1; index < columns; index += 1) {
    const x = inner.x + (index * inner.width) / columns;
    doc.entities.line(toCad({ x, y: inner.y }), toCad({ x, y: inner.y + inner.height }), LAYER_SYMBOLS);
  }
  for (let index = 1; index < rows; index += 1) {
    const y = inner.y + (index * inner.height) / rows;
    doc.entities.line(toCad({ x: inner.x, y }), toCad({ x: inner.x + inner.width, y }), LAYER_SYMBOLS);
  }

  if (!table) return;
  const fontPx = Math.max(6, Math.min(72, Math.round(numericAttr(attrs, 'tableFontSize', 10))));
  const cellW = inner.width / columns;
  const cellH = inner.height / rows;
  const writeCell = (text, col, row, bold = false) => {
    writeTextInRect(
      doc,
      text,
      inner.x + col * cellW,
      inner.y + row * cellH,
      cellW,
      cellH,
      toCad,
      {
        fontPx: bold ? fontPx * 1.04 : fontPx,
        paddingX: Math.max(3, fontPx * 0.45),
        paddingY: Math.max(2, fontPx * 0.25),
        hAlign: 0,
        vAlign: 1,
      },
    );
  };
  table.headers.forEach((header, index) => writeCell(header, index, 0, true));
  table.rows.forEach((row, rowIndex) => {
    row.forEach((cell, columnIndex) => writeCell(cell, columnIndex, rowIndex + 1));
  });
}

function writeDistributionBoardPreview(doc, node, box, toCad) {
  const attrs = node.data?.attributes || {};
  const headers = Array.isArray(attrs.headerData?.value) ? attrs.headerData.value.map((header) => String(header ?? '')) : [];
  if (headers.length === 0) return false;
  const rows = Array.isArray(attrs.rowData?.value) ? attrs.rowData.value : [];
  const columnWidths = Array.isArray(attrs.columnWidths?.value) ? attrs.columnWidths.value : [];
  const scaleRaw = Number(attrs.distributionBoardPreviewScale?.value);
  const scale = Number.isFinite(scaleRaw) && scaleRaw > 0 ? scaleRaw : 1;
  const position = attrs.distributionBoardPreviewPosition?.value;
  const left = typeof position?.x === 'number' ? position.x : 0;
  const top = typeof position?.y === 'number' ? position.y : box.height + 24;
  const originX = box.x + left;
  const originY = box.y + top;
  const widths = headers.map((header, index) => {
    const width = Number(columnWidths[index]);
    if (Number.isFinite(width) && width > 0) return Math.max(96, Math.round(width * scale));
    if (header === 'Circuito') return Math.round(110 * scale);
    if (header === 'Descripción de Carga') return Math.round(240 * scale);
    return Math.round(140 * scale);
  });
  const padding = Math.max(6, Math.round(8 * scale));
  const headerFont = Math.max(11, Math.round(12 * scale));
  const cellFont = Math.max(10, Math.round(11 * scale));
  const headerH = headerFont * 1.2 + padding * 2;
  const rowH = cellFont * 1.2 + padding * 2;
  const totalW = widths.reduce((sum, current) => sum + current, 0);
  const totalH = headerH + rows.length * rowH;
  const xPositions = widths.reduce((acc, current) => {
    acc.push(acc[acc.length - 1] + current);
    return acc;
  }, [originX]);
  const yPositions = [originY, originY + headerH];
  rows.forEach((_, index) => yPositions.push(originY + headerH + (index + 1) * rowH));

  const corners = [
    { x: originX, y: originY },
    { x: originX + totalW, y: originY },
    { x: originX + totalW, y: originY + totalH },
    { x: originX, y: originY + totalH },
  ].map(toCad);
  doc.entities.polyline(corners, true, LAYER_SYMBOLS);
  xPositions.slice(1, -1).forEach((x) => {
    doc.entities.line(toCad({ x, y: originY }), toCad({ x, y: originY + totalH }), LAYER_SYMBOLS);
  });
  yPositions.slice(1, -1).forEach((y) => {
    doc.entities.line(toCad({ x: originX, y }), toCad({ x: originX + totalW, y }), LAYER_SYMBOLS);
  });

  const writeText = (text, col, rowIndex, fontPx) => {
    const value = String(text ?? '').trim();
    if (!value) return;
    const yTop = rowIndex === 0 ? originY : originY + headerH + (rowIndex - 1) * rowH;
    const height = rowIndex === 0 ? headerH : rowH;
    const cap = fontPx * TEXT_CAP_RATIO;
    const center = toCad({
      x: xPositions[col] + widths[col] / 2,
      y: yTop + height / 2,
    });
    doc.entities.text({
      x: center.x,
      y: center.y - cap / 2,
      height: cap,
      content: value,
      layer: LAYER_LABELS,
      hAlign: 1,
      vAlign: 2,
    });
  };
  headers.forEach((header, index) => writeText(header, index, 0, headerFont));
  rows.forEach((row, rowIndex) => {
    headers.forEach((header, columnIndex) => {
      writeText(row?.[header] ?? '', columnIndex, rowIndex + 1, cellFont);
    });
  });
  return true;
}

function writeDrawingFrame(doc, pageWidth, pageHeight, toCad) {
  const minSide = Math.min(pageWidth, pageHeight);
  const band = Math.max(32, Math.min(96, minSide * 0.012));
  const textHeight = Math.max(8, Math.min(18, band * 0.28));
  const outer = { x: 0, y: 0, width: pageWidth, height: pageHeight };
  const inner = {
    x: band,
    y: band,
    width: Math.max(1, pageWidth - band * 2),
    height: Math.max(1, pageHeight - band * 2),
  };
  const rect = ({ x, y, width, height }) => {
    doc.entities.polyline([
      toCad({ x, y }),
      toCad({ x: x + width, y }),
      toCad({ x: x + width, y: y + height }),
      toCad({ x, y: y + height }),
    ], true, LAYER_FRAME);
  };

  rect(outer);
  rect(inner);

  const topBand = { x: inner.x, y: 0, width: inner.width, height: band };
  const bottomBand = { x: inner.x, y: pageHeight - band, width: inner.width, height: band };
  rect(topBand);
  rect(bottomBand);
  for (let index = 1; index < FRAME_COLUMNS; index += 1) {
    const x = inner.x + (index * inner.width) / FRAME_COLUMNS;
    doc.entities.line(toCad({ x, y: 0 }), toCad({ x, y: band }), LAYER_FRAME);
    doc.entities.line(toCad({ x, y: pageHeight - band }), toCad({ x, y: pageHeight }), LAYER_FRAME);
  }
  for (let index = 0; index < FRAME_COLUMNS; index += 1) {
    const x = inner.x + (index + 0.5) * (inner.width / FRAME_COLUMNS);
    const top = toCad({ x, y: band / 2 });
    const bottom = toCad({ x, y: pageHeight - band / 2 });
    const content = String(index + 1);
    doc.entities.text({ x: top.x, y: top.y - textHeight / 2, height: textHeight, content, layer: LAYER_FRAME, hAlign: 1, vAlign: 2 });
    doc.entities.text({ x: bottom.x, y: bottom.y - textHeight / 2, height: textHeight, content, layer: LAYER_FRAME, hAlign: 1, vAlign: 2 });
  }

  const leftBand = { x: 0, y: inner.y, width: band, height: inner.height };
  const rightBand = { x: pageWidth - band, y: inner.y, width: band, height: inner.height };
  rect(leftBand);
  rect(rightBand);
  for (let index = 1; index < FRAME_ROWS; index += 1) {
    const y = inner.y + (index * inner.height) / FRAME_ROWS;
    doc.entities.line(toCad({ x: 0, y }), toCad({ x: band, y }), LAYER_FRAME);
    doc.entities.line(toCad({ x: pageWidth - band, y }), toCad({ x: pageWidth, y }), LAYER_FRAME);
  }
  FRAME_ROW_LABELS.forEach((label, index) => {
    const y = inner.y + (index + 0.5) * (inner.height / FRAME_ROWS);
    const left = toCad({ x: band / 2, y });
    const right = toCad({ x: pageWidth - band / 2, y });
    doc.entities.text({ x: left.x, y: left.y - textHeight / 2, height: textHeight, content: label, layer: LAYER_FRAME, hAlign: 1, vAlign: 2 });
    doc.entities.text({ x: right.x, y: right.y - textHeight / 2, height: textHeight, content: label, layer: LAYER_FRAME, hAlign: 1, vAlign: 2 });
  });
}

function writeDxfCenteredText(doc, text, x, y, width, height, toCad, fontPx, layer = LAYER_LABELS) {
  const content = stripHtml(text);
  if (!content) return;
  const cap = fontPx * TEXT_CAP_RATIO;
  const center = toCad({ x: x + width / 2, y: y + height / 2 });
  doc.entities.text({
    x: center.x,
    y: center.y - cap / 2,
    height: cap,
    content,
    layer,
    hAlign: 1,
    vAlign: 2,
  });
}

function writeDxfTextBlock(doc, block, x, y, width, height, toCad, { boxed = false } = {}) {
  writeTextInRect(doc, block?.text || '', x, y, width, height, toCad, {
    fontPx: TEMPLATE_FLOATING_TEXT_FONT_PX,
    paddingX: boxed ? 18 : 0,
    paddingY: boxed ? 16 : 0,
    hAlign: 0,
    vAlign: 2,
  });
}

async function writeDxfImageBlock(doc, block, x, y, width, height, toCad, pageHeight, loader, warnings) {
  const label = templateImageLabel(block);
  const src = getTemplateImageSrc(block);
  const svgText = await loadTemplateSvgText(block, loader);
  if (svgText) {
    try {
      const geometry = svgToSymbolGeometry(svgText);
      for (const tag of geometry.unsupported) {
        warnings.push(`Elemento SVG no soportado en imagen ${label}: <${tag}>`);
      }
      if (geometry.polylines.length > 0) {
        const blockName = doc.addBlock(`template-${label}`, (writer) =>
          writeSymbolGeometry(writer, geometry, doc.blockEntityLayer()),
        );
        const p = symbolInsertPlacement(geometry.viewBox, geometry.intrinsic, x, y, width, height, 0, pageHeight);
        doc.entities.insert(blockName, p.x, p.y, p.scale, p.rotationDeg, LAYER_SYMBOLS);
        return;
      }
      warnings.push(`Imagen SVG sin geometría visible: ${label}`);
    } catch (err) {
      warnings.push(`Imagen SVG ilegible (${err instanceof Error ? err.message : 'parse error'}): ${label}`);
    }
  }
  if (src && isTemplateRasterImageSrc(src)) {
    warnings.push(`DXF no exporta imagenes PNG, JPG o WebP: ${label}. Te recomendamos cambiarla a SVG.`);
  }
  if (src && /^data:image\//i.test(src)) {
    warnings.push(`DXF no exporta imagenes PNG, JPG o WebP: ${label}. Te recomendamos cambiarla a SVG.`);
  }
  writeDxfCenteredText(doc, label, x, y, width, height, toCad, Math.max(10, Math.min(24, width / 14)));
}

async function writeTemplateDesign(doc, templateSettings, pageWidth, pageHeight, toCad, loader, warnings) {
  const settings = templateSettings && typeof templateSettings === 'object' ? templateSettings : {};
  if (settings.pageOption === 'none') return;
  const blocks = Array.isArray(settings.blocks) ? settings.blocks : [];
  if (blocks.length === 0) return;

  const floatingTextBlocks = blocks.filter((block) => block?.type === 'text');
  const floatingImageBlocks = blocks.filter((block) => block?.type === 'image');
  floatingTextBlocks.forEach((block) => {
    const textWidth = Number.isFinite(Number(block.textWidth)) ? Number(block.textWidth) : TEMPLATE_TEXT_WIDTH_DEFAULT;
    const x = Number.isFinite(Number(block.textX)) ? Number(block.textX) : 80;
    const y = Number.isFinite(Number(block.textY)) ? Number(block.textY) : 120;
    writeDxfTextBlock(doc, block, x, y, textWidth, TEMPLATE_TEXT_BLOCK_BASE_HEIGHT, toCad);
  });
  for (const block of floatingImageBlocks) {
    const imageWidth = clampTemplateImageScaleValue(block.imageScale);
    const ratio = normalizeTemplateImageRatioValue(block.imageRatio);
    const imageHeight = ratio === 'rectangle' ? imageWidth * (3 / 4) : imageWidth;
    const x = Number.isFinite(Number(block.imageX)) ? Number(block.imageX) : Math.max(pageWidth - imageWidth - 80, 0);
    const y = Number.isFinite(Number(block.imageY)) ? Number(block.imageY) : 80;
    await writeDxfImageBlock(doc, block, x, y, imageWidth, imageHeight, toCad, pageHeight, loader, warnings);
  }

  const rows = templateRowsFromBlocks(blocks);
  if (rows.length === 0) return;
  const bounds = computeTemplateContentBounds(rows);
  const anchor = settings.anchor === 'left' ? 'left' : 'right';
  const x = anchor === 'right' ? Math.max(pageWidth - bounds.width, 0) : 0;
  const y = 0;
  let currentY = y + TEMPLATE_ANCHOR_VERTICAL_PADDING;
  const innerX = x + TEMPLATE_ANCHOR_HORIZONTAL_PADDING;
  const innerWidth = Math.max(bounds.width - TEMPLATE_ANCHOR_HORIZONTAL_PADDING * 2, 0);
  const preferredTextWidth = Math.max(bounds.textWidth || TEMPLATE_TEXT_BLOCK_BASE_WIDTH, 80);

  for (const row of rows) {
    const rowColumnCount = row.columns === 2 ? 2 : 1;
    const rowGap = rowColumnCount > 1 ? TEMPLATE_ANCHOR_COLUMN_GAP : 0;
    const rowAvailableWidth = Math.max(innerWidth - rowGap, 0);
    const rowColumnWidth = rowColumnCount > 1
      ? Math.max(rowAvailableWidth / rowColumnCount, TEMPLATE_ANCHOR_MIN_COLUMN_WIDTH)
      : innerWidth;
    let rowHeight = 0;
    row.blocks.forEach((block) => {
      if (block.type === 'image') {
        const imageWidth = Math.min(clampTemplateImageScaleValue(block.imageScale), rowColumnWidth);
        rowHeight = Math.max(rowHeight, normalizeTemplateImageRatioValue(block.imageRatio) === 'rectangle' ? imageWidth * (3 / 4) : imageWidth);
      } else {
        rowHeight = Math.max(rowHeight, TEMPLATE_TEXT_BLOCK_BASE_HEIGHT);
      }
    });
    for (const [index, block] of row.blocks.entries()) {
      const blockX = innerX + index * (rowColumnWidth + TEMPLATE_ANCHOR_COLUMN_GAP);
      if (block.type === 'image') {
        const rawWidth = clampTemplateImageScaleValue(block.imageScale);
        const imageWidth = Math.min(rawWidth, rowColumnWidth);
        const imageHeight = normalizeTemplateImageRatioValue(block.imageRatio) === 'rectangle' ? imageWidth * (3 / 4) : imageWidth;
        await writeDxfImageBlock(doc, block, blockX, currentY, imageWidth, imageHeight, toCad, pageHeight, loader, warnings);
      } else {
        const textWidth = rowColumnCount > 1 ? rowColumnWidth : Math.min(preferredTextWidth, rowColumnWidth || preferredTextWidth);
        writeDxfTextBlock(doc, block, blockX, currentY, textWidth, TEMPLATE_TEXT_BLOCK_BASE_HEIGHT, toCad, { boxed: true });
      }
    }
    currentY += rowHeight + TEMPLATE_ANCHOR_ROW_GAP;
  }
}

export async function buildDxf(
  nodes,
  edges,
  width,
  height,
  loader,
  options = {},
) {
  const doc = new DxfDocument(width, height);
  const warnings = [];
  const toCad = (p) => screenToCad(p, height);
  const includeDesignElements = options.includeDesignElements !== false;
  const exportNodes = includeDesignElements ? nodes : nodes.filter((node) => !isDxfDesignNode(node));
  writeDrawingFrame(doc, width, height, toCad);
  if (includeDesignElements) {
    await writeTemplateDesign(doc, options.templateSettings, width, height, toCad, loader, warnings);
  }

  const nodeType = (node) => node.type || 'customNode';
  const isSymbolNode = (node) => nodeType(node) !== 'busNode' && nodeType(node) !== 'textBoxNode';

  // Load + convert each distinct rendered SVG once (BLOCK cache keyed by resolved URL).
  const symbolCache = new Map();
  const urls = new Set();
  for (const node of exportNodes) {
    if (!isSymbolNode(node)) continue;
    const src = resolveSymbolSrc(selectedVariantSrc(node.data));
    if (src) urls.add(src);
  }
  await Promise.all(
    Array.from(urls).map(async (url) => {
      let text = null;
      try {
        text = await loader.loadText(url);
      } catch {
        text = null;
      }
      if (text == null) {
        warnings.push(`SVG no encontrado: ${url}`);
        symbolCache.set(url, null);
        return;
      }
      try {
        const geometry = svgToSymbolGeometry(text);
        for (const tag of geometry.unsupported) {
          warnings.push(`Elemento SVG no soportado en ${url}: <${tag}>`);
        }
        if (geometry.polylines.length === 0) {
          warnings.push(`SVG sin geometría visible: ${url}`);
        }
        const blockName = doc.addBlock(url.split('/').pop() || url, (w) =>
          writeSymbolGeometry(w, geometry, doc.blockEntityLayer()),
        );
        symbolCache.set(url, { blockName, geometry });
      } catch (err) {
        warnings.push(`SVG ilegible (${err instanceof Error ? err.message : 'parse error'}): ${url}`);
        symbolCache.set(url, null);
      }
    }),
  );

  // Placeholder mirroring the canvas fallback tile: node-box rectangle + centered symbolLabel.
  const writePlaceholder = (node, box) => {
    const corners = [
      { x: box.x, y: box.y },
      { x: box.x + box.width, y: box.y },
      { x: box.x + box.width, y: box.y + box.height },
      { x: box.x, y: box.y + box.height },
    ].map(toCad);
    doc.entities.polyline(corners, true, LAYER_SYMBOLS);
    const center = toCad({ x: box.x + box.width / 2, y: box.y + box.height / 2 });
    doc.entities.text({
      x: center.x,
      y: center.y,
      height: PLACEHOLDER_FONT_PX * TEXT_CAP_RATIO,
      content: String(node.data?.symbolLabel || '?'),
      layer: LAYER_SYMBOLS,
      hAlign: 1,
      vAlign: 2,
    });
  };

  const writeLabelLines = (label, x, y) => {
    const cap = label.fontPx * TEXT_CAP_RATIO;
    const lineHeight = label.fontPx * 1.25;
    label.lines.forEach((line, index) => {
      const point = toCad({ x, y: y + index * lineHeight + cap });
      doc.entities.text({
        x: point.x,
        y: point.y,
        height: cap,
        content: line,
        layer: LAYER_LABELS,
      });
    });
  };

  // Attribute label block to the right of the node box (CustomNode).
  const writeNodeLabel = (node, box) => {
    const label = labelOf(node);
    if (!label) return;
    const textPosition = node.data?.textPosition;
    const labelX = box.x + (typeof textPosition?.x === 'number' ? textPosition.x : box.width + LABEL_GAP_PX);
    const labelY = box.y + (typeof textPosition?.y === 'number' ? textPosition.y : 0);
    writeLabelLines(label, labelX, labelY);
  };

  for (const node of exportNodes) {
    const box = nodeBox(node);
    const type = nodeType(node);

    if (type === 'busNode') {
      // The bus bar fills its node box (BusNode's black div): filled SOLID + outline.
      const corners = [
        toCad({ x: box.x, y: box.y }),
        toCad({ x: box.x + box.width, y: box.y }),
        toCad({ x: box.x + box.width, y: box.y + box.height }),
        toCad({ x: box.x, y: box.y + box.height }),
      ];
      doc.entities.solid(corners, LAYER_SYMBOLS);
      doc.entities.polyline([...corners], true, LAYER_SYMBOLS);
      const label = labelOf(node);
      if (label) {
        // BusNode renders the label above the bar, left-aligned, with the line
        // box's bottom edge 6px above it. Center the cap in that line box (same
        // model as writeNodeLabel) so the baseline matches the canvas.
        const offset = node.data?.labelOffset || {};
        const labelX = box.x + (typeof offset.x === 'number' ? offset.x : 0);
        const labelY = box.y
          + (typeof offset.y === 'number' ? offset.y : -BUS_LABEL_GAP_PX - LABEL_LINE_HEIGHT * label.fontPx);
        writeLabelLines(label, labelX, labelY);
      }
      continue;
    }

    if (type === 'textBoxNode') {
      writeTextBox(doc, node, box, toCad);
      continue;
    }

    if (type === 'boxNode' || node.data?.symbolLabel === BOX_SYMBOL_LABEL) {
      writeBoxNode(doc, node, box, toCad);
      continue;
    }

    if (type === 'straightLineNode' || node.data?.symbolLabel === 'StraightLine') {
      const line = node.data?.line || {};
      const start = line.start || { x: 0, y: 0 };
      const end = line.end || { x: box.width, y: 0 };
      doc.entities.line(
        toCad({ x: box.x + (Number(start.x) || 0), y: box.y + (Number(start.y) || 0) }),
        toCad({ x: box.x + (Number(end.x) || 0), y: box.y + (Number(end.y) || 0) }),
        LAYER_SYMBOLS
      );
      continue;
    }

    if (type === 'symbolDrawerNode' || node.data?.symbolLabel === 'SymbolDrawer') {
      const items = getUsedSymbolDrawerItems(exportNodes);
      const rowHeight = 54;
      const titleHeight = 28;
      const width = Math.max(240, box.width || 320);
      const height = Math.max(80, titleHeight + Math.max(1, items.length) * rowHeight);
      const colWidth = 82;
      const corners = [
        toCad({ x: box.x, y: box.y }),
        toCad({ x: box.x + width, y: box.y }),
        toCad({ x: box.x + width, y: box.y + height }),
        toCad({ x: box.x, y: box.y + height }),
      ];
      doc.entities.polyline([...corners], true, LAYER_SYMBOLS);
      doc.entities.line(toCad({ x: box.x, y: box.y + titleHeight }), toCad({ x: box.x + width, y: box.y + titleHeight }), LAYER_SYMBOLS);
      doc.entities.line(toCad({ x: box.x + colWidth, y: box.y + titleHeight }), toCad({ x: box.x + colWidth, y: box.y + height }), LAYER_SYMBOLS);
      items.forEach((item, index) => {
        const y = box.y + titleHeight + (index + 1) * rowHeight;
        doc.entities.line(toCad({ x: box.x, y }), toCad({ x: box.x + width, y }), LAYER_SYMBOLS);
        const textPoint = toCad({ x: box.x + colWidth + 8, y: y - rowHeight / 2 + 4 });
        doc.entities.text({ x: textPoint.x, y: textPoint.y, height: 8, content: item.name, layer: LAYER_LABELS });
      });
      const titlePoint = toCad({ x: box.x + 12, y: box.y + 18 });
      doc.entities.text({ x: titlePoint.x, y: titlePoint.y, height: 8, content: 'Cajon de simbologia', layer: LAYER_LABELS });
      continue;
    }

    // Symbol node (customNode and unknown types, matching mapDocument's default).
    const src = resolveSymbolSrc(selectedVariantSrc(node.data));
    const symbol = src ? symbolCache.get(src) : null;
    if (!src) warnings.push(`Nodo ${node.id} (${node.data?.symbolLabel || 'sin nombre'}) no tiene SVG asignado`);
    if (symbol) {
      const p = symbolInsertPlacement(symbol.geometry.viewBox, symbol.geometry.intrinsic, box.x, box.y, box.width, box.height, angleOf(node), height);
      doc.entities.insert(symbol.blockName, p.x, p.y, p.scale, p.rotationDeg, LAYER_SYMBOLS);
    } else {
      writePlaceholder(node, box);
    }
    if (node.data?.attributes?.showDistributionBoardPreview?.value === true) {
      writeDistributionBoardPreview(doc, node, box, toCad);
    }
    writeNodeLabel(node, box);
  }

  // Edges: anchored at true handle positions, routed exactly as rendered.
  const nodeById = new Map(exportNodes.map((n) => [n.id, n]));
  for (const edge of edges) {
    const sourceNode = nodeById.get(edge.source);
    const targetNode = nodeById.get(edge.target);
    if (!sourceNode || !targetNode) {
      warnings.push(`Conductor ${edge.id}: nodo ${!sourceNode ? edge.source : edge.target} inexistente`);
      continue;
    }
    const source = edgeAnchor(sourceNode, edge.sourceHandle, 'source');
    const target = edgeAnchor(targetNode, edge.targetHandle, 'target');
    if (!source || !target) {
      warnings.push(`Conductor ${edge.id}: conexión (${!source ? edge.sourceHandle : edge.targetHandle}) sin posición en el nodo`);
      continue;
    }
    const points = edgeRoutePoints(source, target, edge.data?.waypoints).map(toCad);
    if (points.length >= 2) doc.entities.polyline(points, false, LAYER_CONDUCTORS);
  }

  return { dxf: doc.toString(), warnings };
}

/** Text box: border rectangle + visible content lines, per computeTextBoxStyle. */
function writeTextBox(doc, node, box, toCad) {
  const corners = [
    { x: box.x, y: box.y },
    { x: box.x + box.width, y: box.y },
    { x: box.x + box.width, y: box.y + box.height },
    { x: box.x, y: box.y + box.height },
  ].map(toCad);
  doc.entities.polyline(corners, true, LAYER_LABELS);

  const { text, isPlaceholder } = textBoxDisplay(node.data || {});
  if (isPlaceholder) return; // the inspector hint is UI chrome, not content

  const style = computeTextBoxStyle(node.data || {});
  const fontPx = typeof style.fontSize === 'number' ? style.fontSize : DEFAULT_LABEL_FONT_PX;
  const align = style.textAlign === 'left' || style.textAlign === 'right' ? style.textAlign : 'center';
  // padding is "Ypx Xpx" (computeTextBoxStyle).
  const paddingParts = String(style.padding ?? '').split(' ');
  const paddingY = Number.parseFloat(paddingParts[0] ?? '') || 0;
  const paddingX = Number.parseFloat(paddingParts[1] ?? '') || 0;

  const cap = fontPx * TEXT_CAP_RATIO;
  const lineHeight = fontPx * TEXTBOX_LINE_HEIGHT;
  const sourceLines = text.replace(/\r\n?/g, '\n').split('\n');
  const centerY = box.y + box.height / 2;
  const contentW = box.width - 2 * paddingX;
  const contentH = box.height - 2 * paddingY;
  const maxChars = Math.max(1, Math.floor(contentW / (fontPx * AVG_CHAR_WIDTH_EM)));
  const wrappedLines = sourceLines.flatMap((line) => {
    if (line.length <= maxChars) return [line];
    const chunks = [];
    let remaining = line;
    while (remaining.length > maxChars) {
      const breakIndex = remaining.lastIndexOf(' ', maxChars);
      const nextIndex = breakIndex > 0 ? breakIndex : maxChars;
      chunks.push(remaining.slice(0, nextIndex).trimEnd());
      remaining = remaining.slice(nextIndex).trimStart();
    }
    chunks.push(remaining);
    return chunks;
  });
  const maxLines = Math.max(1, Math.floor(contentH / lineHeight));
  const lines = wrappedLines.slice(0, maxLines);

  lines.forEach((line, i) => {
    if (!line.trim()) return;
    const lineCenterY = centerY + (i - (lines.length - 1) / 2) * lineHeight;
    const base = toCad({ x: 0, y: lineCenterY });
    const y = base.y - cap / 2;
    if (align === 'left') {
      doc.entities.text({ x: box.x + paddingX, y, height: cap, content: line, layer: LAYER_LABELS });
    } else if (align === 'right') {
      doc.entities.text({ x: box.x + box.width - paddingX, y, height: cap, content: line, layer: LAYER_LABELS, hAlign: 2 });
    } else {
      doc.entities.text({ x: box.x + box.width / 2, y, height: cap, content: line, layer: LAYER_LABELS, hAlign: 1 });
    }
  });
}
